import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Container,
  Card,
  CardContent,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { ArrowBack, Info, Error, Delete } from '@material-ui/icons';
import {
  PagamentoTipo,
} from 'common/genericTypes';
import { getInstrumentoFinanceiroLongDescription } from 'common/genericDescriptions';
import { NumberField } from 'm2m.components';
import { Attachment as Files } from 'components/uploadDocs';
import { useGenericStyles } from 'common/genericStyles';
import { useDispatch } from 'react-redux';
import { addAlert } from 'store/modules/snackBar';
import FeedbackLoteModal from 'components/feedbackLoteModal/feedbackLoteModal';

export interface LiquidacaoEmLoteItem {
  id: string;
  codigo: string;
  tipo: PagamentoTipo;
  tipoInstrumentoFinanceiro: string;
  puAtualizado: number;
  puMulta?: number;
  puMora?: number;
  dataVencimento: string;
  liquidacaoDireta: boolean;
}

export interface LiquidacaoEmLoteResultItem {
  pagamentoId: string;
  instrumentoCodigo: string;
  tipoPagamento: string;
  sucesso: boolean;
  mensagemErro?: string | null;
}

export interface LiquidacaoEmLoteResult {
  status: 'SUCESSO' | 'PARCIAL' | 'FALHA';
  mensagem: string;
  resumo: {
    totalProcessados: number;
    totalSucesso: number;
    totalFalha: number;
  };
  itens: LiquidacaoEmLoteResultItem[];
}

interface LiquidacaoEmLoteProps {
  itens: LiquidacaoEmLoteItem[];
  onCancel: (wasProcessed?: boolean) => void;
  postSolicitarAprovarConfirmarLiquidacaoEmLote: (
    dispatch: any,
    request: any,
    stackRequest?: boolean
  ) => Promise<any>;
  postDocumentosLiquidacao: (
    dispatch: any,
    formData: FormData,
    stackRequest?: boolean
  ) => Promise<any>;
}

const LiquidacaoEmLote: React.FC<LiquidacaoEmLoteProps> = ({
  itens,
  onCancel,
  postSolicitarAprovarConfirmarLiquidacaoEmLote,
  postDocumentosLiquidacao,
}) => {
  const dispatch = useDispatch();
  const [jurosItems, setJurosItems] = useState<LiquidacaoEmLoteItem[]>([]);
  const [amortizacaoItems, setAmortizacaoItems] = useState<LiquidacaoEmLoteItem[]>([]);
  const [valorPagamentoItems, setValorPagamentoItems] = useState<Record<string, number | null>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [liquidacaoDiretaItems, setLiquidacaoDiretaItems] = useState<{ [key: string]: boolean }>({});
  const [attachmentsItems, setAttachmentsItems] = useState<{ [key: string]: Files[] }>({});
  const [globalAttachment, setGlobalAttachment] = useState<Files[] | null>(null);
  const [multaItems, setMultaItems] = useState<{ [key: string]: number | null }>({});
  const [moraItems, setMoraItems] = useState<{ [key: string]: number | null }>({});
  const [processedItems, setProcessedItems] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [processedStatus, setProcessedStatus] = useState<Record<string, 'success' | 'error' | 'pending'>>({});
  const [isProcessingComplete, setIsProcessingComplete] = useState<boolean>(false);
  const [feedbackData, setFeedbackData] = useState<LiquidacaoEmLoteResult | null>(null);

  const classes = useGenericStyles();

  useEffect(() => {
    if (itens.length > 0) {
      const juros = itens.filter((p) => p.tipo === PagamentoTipo.Juros);
      const amortizacao = itens.filter((p) => p.tipo === PagamentoTipo.Amortizacao);

      setJurosItems(juros);
      setAmortizacaoItems(amortizacao);

      const initialLiquidacaoDireta: { [key: string]: boolean } = {};
      const initialAttachments: { [key: string]: Files[] } = {};
      const initialMulta: { [key: string]: number | null } = {};
      const initialMora: { [key: string]: number | null } = {};
      const initialValorPagamento: { [key: string]: number | null } = {};

      itens.forEach((p) => {
        initialLiquidacaoDireta[p.id] = p.liquidacaoDireta;
        initialAttachments[p.id] = [];
        initialMulta[p.id] = p.puMulta != null ? p.puMulta : null;
        initialMora[p.id] = p.puMora != null ? p.puMora : null;
        initialValorPagamento[p.id] = p.puAtualizado != null ? p.puAtualizado : null;
      });

      setLiquidacaoDiretaItems(initialLiquidacaoDireta);
      setAttachmentsItems(initialAttachments);
      setMultaItems(initialMulta);
      setMoraItems(initialMora);
      setValorPagamentoItems(initialValorPagamento);
    }
  }, [itens]);

  const validateForm = (): boolean => {
    const invalidPuItems: string[] = [];

    Object.entries(valorPagamentoItems).forEach(([itemId, value]) => {
      if (value == null || value <= 0) {
        const item = itens.find((p) => p.id === itemId);
        if (item) {
          invalidPuItems.push(item.codigo);
        }
      }
    });

    if (invalidPuItems.length > 0) {
      dispatch(
        addAlert({
          message: `PU Atualizado deve ser maior que zero: ${invalidPuItems.join(', ')}`,
          severity: 'error',
        })
      );
      return false;
    }

    return true;
  };

  const handleAttachmentsChange = (pagamentoId: string, files: Files[]) => {
    setAttachmentsItems((prev) => {
      const newState = { ...prev };
      newState[pagamentoId] = files;
      return newState;
    });
  };

  const handleGlobalAttachmentChange = (files: Files[]) => {
    setGlobalAttachment(files);
  };

  const handleLiquidacaoDiretaChange = (pagamentoId: string, value: boolean) => {
    setLiquidacaoDiretaItems((prev) => ({
      ...prev,
      [pagamentoId]: value,
    }));
  };

  const handleMultaChange = (pagamentoId: string, value: number | null) => {
    setMultaItems((prev) => ({
      ...prev,
      [pagamentoId]: value,
    }));
  };

  const handleMoraChange = (pagamentoId: string, value: number | null) => {
    setMoraItems((prev) => ({
      ...prev,
      [pagamentoId]: value,
    }));
  };

  const handleConfirm = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setProcessedItems(0);

    const totalItemsCount = itens.length;
    setTotalItems(totalItemsCount);

    try {
      const requestBody = {
        itens: itens.map((item) => ({
          pagamentoId: item.id,
          instrumentoCodigo: item.codigo,
          tipoPagamento: item.tipo.toString(),
          valorPagamento: valorPagamentoItems[item.id] ?? item.puAtualizado ?? 0,
          valorMora: moraItems[item.id] ?? 0,
          valorMulta: multaItems[item.id] ?? 0,
          liquidacaoDireta: liquidacaoDiretaItems[item.id] ?? true,
        })),
      };

      const result = await postSolicitarAprovarConfirmarLiquidacaoEmLote(dispatch, requestBody, true);

      const feedbackResult: LiquidacaoEmLoteResult = {
        status: result.status ?? 'SUCESSO',
        mensagem: result.mensagem ?? '',
        resumo: result.resumo ?? {
          totalProcessados: totalItemsCount,
          totalSucesso: totalItemsCount,
          totalFalha: 0,
        },
        itens: result.itens ?? [],
      };

      const finalStatus: Record<string, 'success' | 'error' | 'pending'> = {};
      feedbackResult.itens.forEach((resultItem) => {
        finalStatus[resultItem.pagamentoId] = resultItem.sucesso ? 'success' : 'error';
      });
      setProcessedStatus(finalStatus);

      const successIds = feedbackResult.itens
        .filter((r) => r.sucesso)
        .map((r) => r.pagamentoId);

      if (successIds.length > 0 && (globalAttachment?.length || Object.values(attachmentsItems).some((a) => a.length > 0))) {
        await enviarDocumentos(successIds);
      }

      setFeedbackData(feedbackResult);
    } catch (error: any) {
      const errorResponse = error?.response?.data;

      const feedbackResult: LiquidacaoEmLoteResult = errorResponse?.status
        ? {
            status: errorResponse.status,
            mensagem: errorResponse.mensagem || 'Erro desconhecido',
            resumo: errorResponse.resumo || {
              totalProcessados: totalItemsCount,
              totalSucesso: 0,
              totalFalha: totalItemsCount,
            },
            itens: errorResponse.itens || [],
          }
        : {
            status: 'FALHA',
            mensagem: error.message || 'Erro desconhecido',
            resumo: {
              totalProcessados: totalItemsCount,
              totalSucesso: 0,
              totalFalha: totalItemsCount,
            },
            itens: itens.map((item) => ({
              pagamentoId: item.id,
              instrumentoCodigo: item.codigo,
              tipoPagamento: item.tipo.toString(),
              sucesso: false,
              mensagemErro: error.message || 'Erro desconhecido',
            })),
          };

      const finalStatus: Record<string, 'success' | 'error' | 'pending'> = {};
      feedbackResult.itens.forEach((resultItem) => {
        finalStatus[resultItem.pagamentoId] = resultItem.sucesso ? 'success' : 'error';
      });
      setProcessedStatus(finalStatus);
      setFeedbackData(feedbackResult);
    } finally {
      setIsSubmitting(false);
      setIsProcessingComplete(true);
    }
  };

  const enviarDocumentos = async (successIds: string[]) => {
    try {
      const formData = new FormData();
      let hasDocuments = false;

      if (globalAttachment && globalAttachment.length > 0 && globalAttachment[0].file) {
        formData.append('ComprovanteGlobal', globalAttachment[0].file);
        successIds.forEach((id) => {
          formData.append('ComprovanteGlobalPagamentoIds', id);
        });
        hasDocuments = true;
      }

      let docIndex = 0;
      itens.forEach((item) => {
        const anexos = attachmentsItems[item.id];
        if (anexos && anexos.length > 0 && successIds.includes(item.id)) {
          anexos.forEach((anexo) => {
            if (anexo.file) {
              formData.append(`Documentos[${docIndex}].PagamentoId`, item.id);
              formData.append(`Documentos[${docIndex}].Arquivo`, anexo.file);
              docIndex++;
              hasDocuments = true;
            }
          });
        }
      });

      if (hasDocuments) {
        await postDocumentosLiquidacao(dispatch, formData, true);
      }
    } catch (docError: any) {
      dispatch(addAlert({
        message: 'Liquidação realizada, porém houve erro ao enviar comprovantes.',
        severity: 'warning',
      }));
    }
  };

  const renderTable = (items: LiquidacaoEmLoteItem[], title: string) => {
    if (items.length === 0) return null;

    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
          <Typography variant="h6" style={{ display: 'flex', alignItems: 'center' }}>
            {title} ({items.length})
            <Tooltip title={`Tabela de ${title.toLowerCase()} para liquidação em lote`}>
              <Info fontSize="small" style={{ marginLeft: '8px', verticalAlign: 'middle', color: '#757575' }} />
            </Tooltip>
          </Typography>
        </div>
        <TableContainer
          component={Paper}
          style={{
            marginBottom: '16px',
            overflowX: 'auto',
            width: '100%',
            maxWidth: '100%',
            boxShadow: 'none',
          }}
        >
          <Table
            style={{
              tableLayout: 'fixed',
              minWidth: '1200px',
            }}
            stickyHeader
          >
            <TableHead>
              <TableRow>
                <TableCell style={{ width: '140px' }}>Código</TableCell>
                <TableCell style={{ width: '110px' }}>Data Vencimento</TableCell>
                <TableCell style={{ width: '150px' }}>Tipo Instrumento</TableCell>
                <TableCell style={{ width: '120px' }}>Liquidação Direta</TableCell>
                <TableCell style={{ width: '180px' }}>PU Atualizado</TableCell>
                <TableCell style={{ width: '170px' }}>PU Multa</TableCell>
                <TableCell style={{ width: '170px' }}>PU Mora</TableCell>
                <TableCell style={{ width: '180px' }}>Comprovante (opcional)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow
                  key={item.id}
                  style={{
                    backgroundColor:
                      processedStatus[item.id] === 'success'
                        ? 'rgba(76, 175, 80, 0.1)'
                        : processedStatus[item.id] === 'error'
                        ? 'rgba(244, 67, 54, 0.1)'
                        : 'inherit',
                  }}
                >
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2">{item.codigo}</Typography>
                    </div>
                  </TableCell>
                  <TableCell>{item.dataVencimento}</TableCell>
                  <TableCell>{getInstrumentoFinanceiroLongDescription[item.tipoInstrumentoFinanceiro] || item.tipoInstrumentoFinanceiro}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Button
                        variant={liquidacaoDiretaItems[item.id] ? 'contained' : 'outlined'}
                        color={liquidacaoDiretaItems[item.id] ? 'primary' : 'default'}
                        onClick={() => handleLiquidacaoDiretaChange(item.id, true)}
                        size="small"
                        style={{ marginRight: '4px', minWidth: '40px', padding: '2px 8px' }}
                        disabled={isProcessingComplete}
                      >
                        Sim
                      </Button>
                      <Button
                        variant={!liquidacaoDiretaItems[item.id] ? 'contained' : 'outlined'}
                        color={!liquidacaoDiretaItems[item.id] ? 'primary' : 'default'}
                        onClick={() => handleLiquidacaoDiretaChange(item.id, false)}
                        size="small"
                        style={{ minWidth: '40px', padding: '2px 8px' }}
                        disabled={isProcessingComplete}
                      >
                        Não
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <NumberField
                      value={valorPagamentoItems[item.id]}
                      onChange={(value) => {
                        setValorPagamentoItems((prev) => ({
                          ...prev,
                          [item.id]: value as number,
                        }));
                      }}
                      numberType="eightDecimals"
                      adornment={'R$'}
                      adornmentPosition={'start'}
                      disabled={isProcessingComplete}
                      acceptZero
                    />
                  </TableCell>
                  <TableCell>
                    <NumberField
                      value={multaItems[item.id]}
                      onChange={(value) => {
                        handleMultaChange(item.id, value as number);
                      }}
                      numberType="eightDecimals"
                      adornment={'R$'}
                      adornmentPosition={'start'}
                      disabled={isProcessingComplete}
                      acceptZero
                    />
                  </TableCell>
                  <TableCell>
                    <NumberField
                      value={moraItems[item.id]}
                      onChange={(value) => {
                        handleMoraChange(item.id, value as number);
                      }}
                      numberType="eightDecimals"
                      adornment={'R$'}
                      adornmentPosition={'start'}
                      disabled={isProcessingComplete}
                      acceptZero
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column">
                      <Button
                        variant="outlined"
                        size="small"
                        style={{ fontSize: '0.75rem', padding: '2px 8px', textTransform: 'none' }}
                        onClick={() => {
                          const fileInput = document.createElement('input');
                          fileInput.type = 'file';
                          fileInput.onchange = (e: any) => {
                            if (e.target.files && e.target.files.length > 0) {
                              const file = e.target.files[0];
                              const files = [
                                {
                                  id: Math.random().toString(36).substring(2),
                                  file: file,
                                },
                              ];
                              handleAttachmentsChange(item.id, files as Files[]);
                            }
                          };
                          fileInput.click();
                        }}
                        disabled={isProcessingComplete}
                      >
                        ANEXAR ARQUIVO ({attachmentsItems[item.id]?.length || 0})
                      </Button>
                      {attachmentsItems[item.id]?.length > 0 && (
                        <Box display="flex" alignItems="center" mt={0.5}>
                          <Typography
                            variant="caption"
                            style={{
                              color: '#4caf50',
                              fontSize: '0.75rem',
                              maxWidth: '180px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={attachmentsItems[item.id][0]?.file?.name}
                          >
                            {attachmentsItems[item.id][0]?.file?.name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => handleAttachmentsChange(item.id, [])}
                            disabled={isProcessingComplete}
                            style={{ marginLeft: '4px', color: '#f44336' }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                      {attachmentsItems[item.id]?.length === 0 && globalAttachment && globalAttachment.length > 0 && (
                        <Typography
                          variant="caption"
                          style={{ color: '#4caf50', marginTop: '4px', fontSize: '0.65rem' }}
                        >
                          Usando anexo global
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  };

  return (
    <Container maxWidth={false} style={{ width: '100%', padding: '0 16px', overflow: 'hidden' }}>
      <Card style={{ marginTop: '16px', marginBottom: '16px', width: '100%', overflowX: 'auto' }}>
        <CardContent style={{ padding: '16px', overflowX: 'auto' }}>
          <Grid container direction="column" spacing={2} style={{ minWidth: '100%', overflowX: 'auto' }}>
            <Grid item>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Button
                  startIcon={<ArrowBack />}
                  variant="text"
                  color="primary"
                  disabled={isSubmitting}
                  onClick={() => {
                    const wasProcessed = isProcessingComplete;

                    setProcessedStatus({});
                    setIsProcessingComplete(false);
                    setProcessedItems(0);
                    setTotalItems(0);
                    onCancel(wasProcessed);
                  }}
                >
                  {isProcessingComplete ? 'Retornar' : 'Cancelar'}
                </Button>
                <Typography variant="h5">Liquidação em Lote</Typography>
              </Box>
              <Divider style={{ marginTop: '16px', marginBottom: '16px' }} />
            </Grid>

            <Grid item>
              <Typography variant="subtitle1" style={{ marginBottom: '16px' }}>
                Selecione os valores para liquidação em lote dos instrumentos selecionados.
                <br />
                Total de itens selecionados: {itens.length}
              </Typography>

              <Box display="flex" alignItems="center" bgcolor="#f5f5f5" p={2} borderRadius={4} mb={2} flexWrap="wrap">
                <Typography variant="subtitle2" style={{ marginRight: '16px', fontWeight: 'bold' }}>
                  Anexo Global:
                </Typography>
                <Typography variant="body2" style={{ marginRight: '16px', color: '#666' }}>
                  Este arquivo será usado para todos os pagamentos que não possuem anexo específico
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  style={{ fontSize: '0.75rem', padding: '4px 12px', textTransform: 'none' }}
                  onClick={() => {
                    const fileInput = document.createElement('input');
                    fileInput.type = 'file';
                    fileInput.onchange = (e: any) => {
                      if (e.target.files && e.target.files.length > 0) {
                        const file = e.target.files[0];
                        const files = [
                          {
                            id: Math.random().toString(36).substring(2),
                            file: file,
                          },
                        ];
                        handleGlobalAttachmentChange(files as Files[]);
                      }
                    };
                    fileInput.click();
                  }}
                  disabled={isProcessingComplete}
                >
                  ANEXAR ARQUIVO GLOBAL ({globalAttachment?.length || 0})
                </Button>
                {globalAttachment && globalAttachment.length > 0 && (
                  <Box ml={2} display="flex" alignItems="center">
                    <Typography
                      variant="body2"
                      style={{
                        color: '#4caf50',
                        maxWidth: '400px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={globalAttachment[0]?.file?.name}
                    >
                      {globalAttachment[0]?.file?.name}
                    </Typography>
                    <IconButton size="small" onClick={() => setGlobalAttachment(null)} style={{ marginLeft: '4px' }}>
                      <Delete fontSize="small" style={{ color: '#f44336' }} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item style={{ width: '100%', overflowX: 'auto' }}>
              {jurosItems.length > 0 && renderTable(jurosItems, 'Juros')}
            </Grid>

            <Grid item style={{ width: '100%', overflowX: 'auto' }}>
              {amortizacaoItems.length > 0 && renderTable(amortizacaoItems, 'Amortizações')}
            </Grid>

            {itens.length === 0 && (
              <Grid item>
                <Paper style={{ padding: '24px', textAlign: 'center' }}>
                  <Error color="error" style={{ fontSize: '48px', marginBottom: '16px' }} />
                  <Typography variant="h6">Nenhum item selecionado para liquidação</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Retorne à tela anterior e selecione os itens que deseja liquidar em lote.
                  </Typography>
                </Paper>
              </Grid>
            )}

            <Grid item>
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button
                  onClick={() => {
                    const wasProcessed = isProcessingComplete;

                    setProcessedStatus({});
                    setIsProcessingComplete(false);
                    setProcessedItems(0);
                    setTotalItems(0);
                    onCancel(wasProcessed);
                  }}
                  variant="outlined"
                  style={{ marginRight: '16px' }}
                  disabled={isSubmitting}
                >
                  {isProcessingComplete ? 'Retornar' : 'Cancelar'}
                </Button>
                {!isProcessingComplete && (
                  <Button
                    onClick={handleConfirm}
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || itens.length === 0}
                  >
                    {isSubmitting ? (
                      <>
                        <CircularProgress size={20} color="inherit" style={{ marginRight: '8px' }} />
                        {`Processando (${processedItems}/${totalItems})`}
                      </>
                    ) : (
                      'Confirmar Solicitação Liquidação'
                    )}
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {feedbackData && (
        <FeedbackLoteModal
          open={!!feedbackData}
          onClose={() => {
            const shouldExit = feedbackData.status !== 'FALHA';
            setFeedbackData(null);
            if (shouldExit) {
              onCancel(true);
            } else {
              setIsProcessingComplete(false);
            }
          }}
          data={{
            status: feedbackData.status,
            titulo: feedbackData.status === 'SUCESSO'
              ? 'Liquidação processada com sucesso'
              : feedbackData.status === 'PARCIAL'
              ? 'Liquidação processada parcialmente'
              : 'Liquidação falhou',
            resumo: feedbackData.resumo,
            erros: feedbackData.itens
              .filter((i) => !i.sucesso)
              .map((item) => ({
                identificador: item.instrumentoCodigo,
                campos: [
                  { label: 'Instrumento', valor: item.instrumentoCodigo },
                  { label: 'Tipo', valor: item.tipoPagamento },
                ],
                mensagemErro: item.mensagemErro ?? 'Erro desconhecido',
              })),
          }}
          tituloFalha="Falhas na liquidação"
        />
      )}
    </Container>
  );
};

export default LiquidacaoEmLote;
